import { createFileRoute, notFound } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import { isUuid } from '@leanin/shared';
import type { GroupId } from '@leanin/shared/ids';
import { trackGroupViewed } from '@/lib/analytics/events';
import { isGroupRef } from '@/lib/group-url';
import { useMember, memberPhotoUrl } from '@/lib/use-session';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { useFormatDate } from '@/lib/format-date.client';
import { NetworkDetailView } from '@/components/networks/NetworkDetailView';
import { groupDetailToNetworkVM } from '@/components/networks/adapters';
import { useEventAdapterContext } from '@/components/events/event-adapters.client';
import type { TabId } from '@/components/networks/NetworkDetailView';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import {
  groupKeys,
  useInfiniteGroupMembers,
  useInfiniteNetworkCircles,
} from '@/lib/queries/groups';
import { useInfiniteFeed } from '@/lib/queries/feed';

const VALID_TABS: ReadonlyArray<TabId> = [
  'overview',
  'feed',
  'members',
  'circles',
  'events',
  'resources',
];

// An explicit, valid `?tab` wins; anything else (absent or unknown) resolves
// to `undefined` so the URL carries no tab claim. The landing default is then
// owned solely by NetworkDetailView, which picks Feed vs Overview from the
// viewer's roster membership (issue #284) — a decision this router-level
// validator can't make, since it never sees the membership.
function toTabId(v: unknown): TabId | undefined {
  if (typeof v === 'string' && (VALID_TABS as ReadonlyArray<string>).includes(v))
    return v as TabId;
  return undefined;
}

type Search = { tab?: TabId; compose?: true; post?: string; join?: true };

// `?compose=1` opens the Feed tab's composer on landing (join-link "Say hello"
// step). A bare compose intent implies the Feed tab. `?join=1` opens the
// Request-to-Join modal on landing — set by /auth/finishing when a referral
// signup's auto-join was refused because the group requires the intake form.
function toFlag(v: unknown): boolean {
  return v === '1' || v === 1 || v === true || v === 'true';
}

export const Route = createFileRoute('/_public/networks/$id/')({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const compose = toFlag(raw.compose);
    const join = toFlag(raw.join);
    const tab = compose && raw.tab === undefined ? 'feed' : toTabId(raw.tab);
    // `?post=<id>` scrolls the Feed tab to that post on landing (overview
    // activity preview's "Load more"). Only meaningful on the feed tab.
    const post =
      typeof raw.post === 'string' && raw.post !== '' && tab === 'feed'
        ? raw.post
        : undefined;
    return {
      ...(tab !== undefined ? { tab } : {}),
      ...(compose ? { compose: true } : {}),
      ...(post !== undefined ? { post } : {}),
      ...(join ? { join: true } : {}),
    };
  },
  beforeLoad: ({ params }) => {
    // The detail GET resolves slugs as well as uuids; anything else is a 404.
    if (!isGroupRef(params.id)) throw notFound();
  },
  component: NetworkDetailPage,
});

// Networks doesn't have a visitor/member split that requires a separate
// component — every role is handled by NetworkDetailView, which renders
// progressively from skeleton chrome (header + aside + tab bodies) to the
// fully-loaded view as each query lands. The role query is the only gate
// for leader-only fetches (`enabled: isLeader`); the rest stream in.
//
// Anonymous viewers (no session) get the same shape with `viewer` set to
// null and the feed / role / roster / join-requests queries disabled.
// The Feed and Members tabs are hidden by NetworkDetailView when viewer
// is null (member-only surfaces); Request-to-Join links to /get-started.
function NetworkDetailPage() {
  const { id } = Route.useParams();
  const { compose, post, join } = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const me = useMember();
  const signedIn = me !== null;
  const t = useTranslations('Networks');
  const fmt = useFormatDate();
  const eventCtx = useEventAdapterContext();

  // Resolver: the detail GET is the only endpoint that accepts a slug, so
  // this query keys by the raw route param and yields the canonical uuid.
  // For uuid params it shares the display query's key (one cache entry).
  const refQuery = useQuery(groupKeys.network.detail(id));
  const groupId = refQuery.data?.id ?? (isUuid(id) ? id : null);
  const resolved = groupId !== null;

  // Display query — always uuid-keyed, because every mutation-side cache
  // patch and invalidation (lib/mutations/groups.ts, lib/cache/
  // group-invalidation.ts) targets `detail(uuid)` and must reach the page
  // even when it's slug-addressed. Seeded from the resolver's data so a
  // slug arrival renders in one load. Every sub-resource endpoint is
  // uuid-only, so the child queries below key off `groupId` too and stay
  // disabled until it's known.
  const detailQuery = useQuery({
    ...groupKeys.network.detail(groupId ?? ''),
    enabled: resolved,
    ...(refQuery.data !== undefined ? { initialData: refQuery.data } : {}),
  });

  // Canonical URL: the slug is the address-bar form. A uuid arrival (all
  // internal links use uuids) is replaced with the slug once the detail
  // loads. The slug-keyed resolver entry is seeded with the same data
  // first, so the remount resolves synchronously — no visible reload; the
  // display and child queries are uuid-keyed and don't re-key at all.
  useEffect(() => {
    const detail = detailQuery.data;
    if (detail === undefined || detail.slug === null || detail.slug === id) {
      return;
    }
    queryClient.setQueryData(
      groupKeys.network.detail(detail.slug).queryKey,
      detail,
    );
    void navigate({
      to: '/networks/$id',
      params: { id: detail.slug },
      search: (current) => current,
      replace: true,
    });
  }, [detailQuery.data, id, navigate, queryClient]);

  const leadersQuery = useQuery({
    ...groupKeys.network.leaders(groupId ?? ''),
    enabled: resolved,
  });
  const circlesQuery = useInfiniteNetworkCircles(groupId ?? '', {
    includeArchived: false,
    pageSize: 24,
    enabled: resolved,
  });
  const eventsQuery = useQuery({
    ...groupKeys.network.events(groupId ?? '', {
      window: 'upcoming',
      pageSize: 4,
    }),
    enabled: resolved,
  });
  // Keyed under feedKeys so useCreateFeedPost/useReactToFeedPost/etc.
  // invalidations under feedKeys.lists() reach this query too — without
  // that, a post created via the Group composer never appears in this
  // detail page's feed tab until a hard reload. Disabled for anon — the
  // feed endpoint is member-only and the tab itself is hidden.
  const postsQuery = useInfiniteFeed(
    { scope: `group:${groupId ?? ''}` },
    { enabled: signedIn && resolved },
  );
  const roleQuery = useQuery({
    ...groupKeys.viewerAccess(groupId ?? ''),
    select: (access) => access.role,
    enabled: signedIn && resolved,
  });

  const rawRole = roleQuery.data;
  const role: 'leader' | 'member' | null =
    rawRole === 'leader' ? 'leader' : rawRole === 'member' ? 'member' : null;
  const isLeader = role === 'leader';

  // Roster search: the displayed value updates immediately; the debounced
  // value drives the query key so the server only gets called 300ms after
  // the user stops typing — avoids a request-per-keystroke.
  const [rosterSearchInput, setRosterSearchInput] = useState('');
  const rosterSearchDebounced = useDebouncedValue(rosterSearchInput, 300);

  const rosterQuery = useInfiniteGroupMembers(groupId ?? '', {
    pageSize: 24,
    enabled: signedIn && resolved,
    search: rosterSearchDebounced || undefined,
  });
  const joinRequestsQuery = useQuery({
    ...groupKeys.network.joinRequests(groupId ?? ''),
    enabled: isLeader && resolved,
  });

  // Once per network, only after the canonical-slug replace above has settled
  // — a uuid arrival remounts this component under the slug param, and firing
  // on both mounts would double-count a single view. Signed-in viewers wait
  // for the role query so is_member/is_leader reflect the real membership.
  const viewedNetworkIdRef = useRef<string | null>(null);
  useEffect(() => {
    const detail = detailQuery.data;
    if (detail === undefined) return;
    if (detail.slug !== null && detail.slug !== id) return;
    if (signedIn && !roleQuery.isSuccess) return;
    if (viewedNetworkIdRef.current === detail.id) return;
    viewedNetworkIdRef.current = detail.id;
    trackGroupViewed({
      group_type: 'network',
      is_member: role !== null,
      is_leader: isLeader,
    });
  }, [detailQuery.data, id, signedIn, roleQuery.isSuccess, role, isLeader]);

  // 404 → branded NotFoundPage directly (render-context `throw notFound()`
  // goes to TanStack's catch-boundary, not its 404 surface). A slug arrival
  // fails on the resolver (the display query never enables), so check both.
  if (isNotFoundApiError(refQuery.error) || isNotFoundApiError(detailQuery.error))
    return <NotFoundPage />;
  // Other errors after the detail query has had a chance to fail — route
  // to the catch-boundary so the loading skeleton doesn't render forever.
  if ((refQuery.isError || detailQuery.isError) && !detailQuery.data)
    throw notFound();

  // Aggregate VM when the base slices (detail + leaders + circles + events)
  // are all in. Feed / roster / pending-join-requests stream independently
  // (passed alongside as their queries resolve).
  const detail = detailQuery.data ?? null;
  const leaders = leadersQuery.data?.items ?? null;
  const circles = circlesQuery.data?.pages.flatMap((p) => p.items) ?? null;
  const events = eventsQuery.data?.items ?? null;
  const feedItems = postsQuery.data?.pages.flatMap((p) => p.items) ?? null;
  const rosterItems = rosterQuery.data?.pages.flatMap((p) => p.items) ?? null;
  // fetchGroupJoinRequests already scopes to pending — no re-filter here.
  const pendingJoinRequests = isLeader
    ? (joinRequestsQuery.data?.items ?? [])
    : [];

  const network =
    detail !== null && leaders !== null && circles !== null && events !== null
      ? groupDetailToNetworkVM(
          detail,
          leaders,
          circles,
          events,
          role,
          {
            posts: feedItems ?? [],
            networkId: detail.id as GroupId,
            rosterItems: rosterItems ?? [],
            pendingJoinRequests,
          },
          {
            eventCtx,
            leaderJoinedRole: (joinedAt) =>
              t('fallbacks.joinedRole', { date: fmt.medium(joinedAt) }),
          },
        )
      : null;

  const viewerPhoto = me ? memberPhotoUrl(me) : null;

  return (
    <NetworkDetailView
      networkId={groupId ?? id}
      network={network}
      viewerRole={role}
      viewer={
        me
          ? {
              id: me.id,
              name: `${me.firstName} ${me.lastName}`.trim(),
              photoUrl: viewerPhoto ?? null,
            }
          : null
      }
      feedItems={feedItems}
      postsHasMore={postsQuery.hasNextPage}
      onPostsLoadMore={() => void postsQuery.fetchNextPage()}
      isPostsLoadingMore={postsQuery.isFetchingNextPage}
      composeOnLoad={compose === true}
      {...(post !== undefined ? { scrollToPostId: post } : {})}
      joinOnLoad={join === true}
      roster={network !== null ? network.roster : null}
      rosterHasMore={rosterQuery.hasNextPage}
      onRosterLoadMore={() => void rosterQuery.fetchNextPage()}
      circlesHasMore={circlesQuery.hasNextPage}
      onCirclesLoadMore={() => void circlesQuery.fetchNextPage()}
      isCirclesLoadingMore={circlesQuery.isFetchingNextPage}
      rosterSearch={rosterSearchInput}
      onRosterSearch={setRosterSearchInput}
    />
  );
}
